import { request } from './api-client';
export type ConnectionAuth =
  | { mode: 'none' }
  | { mode: 'bearer'; token: string }
  | { mode: 'mtls'; certificateChainPem: string; privateKeyPem: string };
export interface ConnectionCapability {
  fingerprint: string;
  id: string;
  endpoint_id: string;
  remote_id: string;
  kind: string;
  title: string;
  description: string;
  input_schema: Record<string, unknown> | null;
  selected: boolean;
  policy: 'review' | 'read';
  available: boolean;
}
export interface ConnectionEndpoint {
  id: string;
  protocol: 'mcp' | 'a2a';
  url: string;
  status: string;
  safe_error: string | null;
  authenticated: boolean;
  auth?: {
    mode: 'none' | 'bearer' | 'mtls';
    configured: boolean;
    revision: number;
    fingerprint?: string;
    expiresAt?: string;
    renewalWarning?: boolean;
  };
  metadata: {
    identity?: unknown;
    protocolVersion?: string;
    reviewRequiredTools?: string[];
    replaySafeTools?: Array<{
      tool: string;
      keyArgument: string;
      statusTool: string;
      statusKeyArgument: string;
    }>;
  };
  valid_until: string | null;
}
export interface Connection {
  id: string;
  name: string;
  enabled: boolean;
  revision: number;
  generation: number;
  instructions: string;
  instructions_revision: number;
  state: string;
  disconnected_at: string | null;
  endpoints: ConnectionEndpoint[];
  capabilities: ConnectionCapability[];
}
export interface ConnectionOperation {
  inputs?: Array<{ messageId: string; text: string; sharedAt: string }>;
  id: string;
  connection_id: string;
  capability_id: string;
  request: Record<string, unknown>;
  request_hash: string;
  revision: number;
  status: string;
  dispatched_at: string | null;
  remote_state: string | null;
  remote_task_id: string | null;
  result: Record<string, unknown> | null;
  safe_error: string | null;
  conversation_id: string | null;
}
export interface NavigationStatus {
  asOf: string;
  validUntil: string;
  sequences: { ready: boolean; reason?: string };
  connections: { ready: boolean; reason?: string };
}
export interface ConnectionSetupDefaults {
  starter: 'brain' | 'stripe' | 'agent' | 'custom';
  name?: string;
  protocol?: 'mcp' | 'a2a';
  url?: string;
  instructions?: string;
}
export interface ConnectionSetupDraft {
  id: string;
  defaults: ConnectionSetupDefaults;
  cancelled: boolean;
  removed: boolean;
  connection: Connection | null;
}
export const connectionApi = {
  setupDraft: (id: string) => request<ConnectionSetupDraft>(`/v1/connection-setups/${id}`),
  cancelSetup: (id: string) => request<ConnectionSetupDraft>(`/v1/connection-setups/${id}/cancel`, { method: 'POST', body: {} }),
  list: (cursor?: string) =>
    request<{ items: Connection[]; nextCursor: string | null }>(
      `/v1/connections${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  detail: (id: string) => request<Connection>(`/v1/connections/${id}`),
  setup: (body: unknown) =>
    request<Connection>('/v1/connections/setup', { method: 'POST', body }),
  patch: (id: string, body: unknown) =>
    request<Connection>(`/v1/connections/${id}`, { method: 'PATCH', body }),
  replaceCredential: (
    id: string,
    endpointId: string,
    expectedRevision: number,
    auth: ConnectionAuth,
  ) =>
    request<Connection>(
      `/v1/connections/${id}/endpoints/${endpointId}/credential`,
      { method: 'PUT', body: { expectedRevision, auth } },
    ),
  check: (id: string) =>
    request<Connection>(`/v1/connections/${id}/check`, {
      method: 'POST',
      body: {},
    }),
  disconnect: (id: string, expectedRevision: number) =>
    request<Connection>(`/v1/connections/${id}/disconnect`, {
      method: 'POST',
      body: { expectedRevision },
    }),
  remove: (id: string, expectedRevision: number) =>
    request<void>(`/v1/connections/${id}`, {
      method: 'DELETE', body: { expectedRevision },
    }),
  operations: (id: string) =>
    request<{ items: ConnectionOperation[] }>(
      `/v1/connections/${id}/operations`,
    ),
  operation: (id: string) =>
    request<ConnectionOperation>(`/v1/connection-operations/${id}`),
  command: (id: string, action: string, body: unknown) =>
    request<ConnectionOperation>(`/v1/connection-operations/${id}/${action}`, {
      method: 'POST',
      body,
    }),
  prepare: (id: string, body: unknown) =>
    request<ConnectionOperation>(`/v1/connections/${id}/operations`, {
      method: 'POST',
      body,
    }),
  navigation: () => request<NavigationStatus>('/v1/navigation/status'),
};
export function statusLabel(state: string) {
  return (
    (
      {
        ready: 'Ready',
        off: 'Off',
        checking: 'Checking',
        offline: 'Offline',
        reconnect_required: 'Reconnect required',
        needs_setup: 'Needs setup',
        auth_required: 'Reconnect required',
        unavailable: 'Unavailable',
        disconnecting: 'Disconnecting',
        disconnected: 'Disconnected',
      } as Record<string, string>
    )[state] ?? state.replaceAll('_', ' ')
  );
}
